<template>
  <div id="containerMenu">
    <v-menu
      v-model="showMenu"
      attach="#containerMenu"
      offset-y
      content-class="mt-2"
      origin="top center"
      :nudge-left="offsetMenuX"
      :close-on-content-click="false"
    >
      <template v-slot:activator="{on, attrs, value}">
        <div ref="activator" style="position: relative">
          <z-icon color="var(--black-800)" v-bind="attrs" v-on="on">
            $apps_rounded
          </z-icon>

          <v-fade-transition>
            <div v-if="value" class="arrow-menu"></div>
          </v-fade-transition>
        </div>
      </template>

      <z-card width="500px">
        <template #title>
          <z-input
            v-show="showSearch"
            key="search"
            ref="searchInput"
            style="padding-bottom: 5px"
            hide-details
            small
            :label="$t('search_page')"
            :value="searchValue"
            @input="onSearchPage"
          >
            <template #append>
              <z-icon
                style="margin-top: 2px"
                color="var(--black)"
                @click="setSearchVisibility(false)"
              >
                $clear_rounded
              </z-icon>
            </template>
          </z-input>

          <div v-show="!showSearch" key="title">
            <div
              class="d-flex justify-space-between align-center"
              style="padding-bottom: 15px"
            >
              <div class="card-title">{{ title }}</div>

              <z-icon
                color="var(--black)"
                size="20"
                class="pa-0"
                style="padding-bottom: 5px"
                @click="setSearchVisibility(true)"
              >
                $search_rounded
              </z-icon>
            </div>
          </div>
        </template>

        <template #content>
          <div class="menu-content-container" style="position: relative">
            <z-chip-tabs
              v-model="tabValue"
              class="pa-0"
              :class="{'disabled-slider': showSearch}"
              :tabs="tabs"
              @input="searchValue = ''"
            >
              <template #all>
                <v-slide-x-transition leave-absolute hide-on-leave>
                  <div
                    v-show="computedBreadcrumbs.length > 1"
                    key="breadcrumbs"
                  >
                    <div class="d-flex" style="padding-bottom: 15px">
                      <z-icon class="mr-4 pa-0" @click="onClickArrowBack">
                        $arrow_back_rounded
                      </z-icon>

                      <z-breadcrumbs
                        ref="breadcrumb"
                        class="pa-0"
                        :items="computedBreadcrumbs"
                      >
                        <template #item="{item}">
                          <div
                            tabindex="0"
                            @click="onClickBreadcrumb(item)"
                            @keydown.enter="onClickBreadcrumb(item)"
                          >
                            <z-overflow-tooltip
                              transition="fade-transition"
                              :class-name="`breadcrumb-custom-item ${
                                isItemActive(item) ? 'active' : ''
                              }`"
                              :open-delay="1500"
                              :content="item.title"
                            >
                            </z-overflow-tooltip>
                          </div>
                        </template>

                        <template #divider>
                          <z-icon size="15">$arrow_right_alt_rounded</z-icon>
                        </template>
                      </z-breadcrumbs>
                    </div>
                  </div>
                </v-slide-x-transition>

                <z-scrollbar
                  ref="scrollbar"
                  style="max-height: clamp(100px, 400px, 50vh)"
                >
                  <v-window key="window" :value="currentLevel">
                    <v-window-item
                      :value="1"
                      :reverse-transition="firstLevelTransitions.back"
                      :transition="firstLevelTransitions.foward"
                    >
                      <div
                        :class="{
                          'options-container': true,
                          empty:
                            (searchValue && !searchOptions.length) ||
                            (!searchValue && !options.length),
                        }"
                      >
                        <template v-if="searchValue && searchOptions.length">
                          <option-item
                            v-for="(option, index) of searchOptions"
                            :key="index"
                            :disabled="option.disabled"
                            :title="option.title"
                            :description="option.description"
                            :icon="option.icon"
                            :icon-bg-color="option.iconColor"
                            :is-favorite="option.favorite"
                            :is-route="
                              !Boolean(
                                option.children && option.children.length
                              )
                            "
                            @favorite="onFavorite(option)"
                            @click="onClickOption(option)"
                          ></option-item>
                        </template>

                        <template
                          v-else-if="!searchValue && options.length > 0"
                        >
                          <option-item
                            v-for="(option, index) of options"
                            :key="index"
                            :disabled="option.disabled"
                            :title="option.title"
                            :description="option.description"
                            :icon="option.icon"
                            :icon-bg-color="option.iconColor"
                            :is-favorite="option.favorite"
                            :is-route="
                              !Boolean(
                                option.children && option.children.length
                              )
                            "
                            @favorite="onFavorite(option)"
                            @click="onClickLevel(option)"
                          ></option-item>
                        </template>

                        <template v-else>
                          <div class="empty-text">
                            {{ $t('empty_text') }}
                          </div>
                        </template>
                      </div>
                    </v-window-item>

                    <v-window-item :value="2">
                      <div class="options-container">
                        <option-item
                          v-for="(option, index) of childrenL1"
                          :key="index"
                          :disabled="option.disabled"
                          :title="option.title"
                          :description="option.description"
                          :icon="option.icon"
                          :icon-bg-color="option.iconColor"
                          :is-favorite="option.favorite"
                          :is-route="
                            !Boolean(option.children && option.children.length)
                          "
                          @favorite="onFavorite(option)"
                          @click="onClickChildLevel(option)"
                        ></option-item>
                      </div>
                    </v-window-item>

                    <v-window-item :value="3">
                      <div class="options-container">
                        <option-item
                          v-for="(option, index) of childrenL2"
                          :key="index"
                          :disabled="option.disabled"
                          :title="option.title"
                          :description="option.description"
                          :icon="option.icon"
                          :icon-bg-color="option.iconColor"
                          :is-favorite="option.favorite"
                          :is-route="
                            !Boolean(option.children && option.children.length)
                          "
                          @click="onClickOption(option)"
                          @favorite="onFavorite(option)"
                        ></option-item>
                      </div>
                    </v-window-item>
                  </v-window>
                </z-scrollbar>
              </template>

              <template #favorites>
                <z-scrollbar style="max-height: clamp(100px, 400px, 50vh)">
                  <div
                    :class="{
                      'options-container': true,
                      empty:
                        (searchValue && !favoritesSearchOptions.length) ||
                        (!searchValue && !favorites.length),
                    }"
                  >
                    <template
                      v-if="searchValue && favoritesSearchOptions.length"
                    >
                      <option-item
                        v-for="(option, index) of favoritesSearchOptions"
                        :key="index"
                        :disabled="option.disabled"
                        :title="option.title"
                        :description="option.description"
                        :icon="option.icon"
                        :icon-bg-color="option.iconColor"
                        :is-favorite="option.favorite"
                        :is-route="
                          !Boolean(option.children && option.children.length)
                        "
                        @click="onClickOption(option)"
                        @favorite="onFavorite(option)"
                      ></option-item>
                    </template>

                    <template v-else-if="!searchValue && favorites.length > 0">
                      <option-item
                        v-for="(option, index) of favorites"
                        :key="index"
                        :title="option.title"
                        :disabled="option.disabled"
                        :description="option.description"
                        :icon="option.icon"
                        :icon-bg-color="option.iconColor"
                        :is-favorite="option.favorite"
                        :is-route="
                          !Boolean(option.children && option.children.length)
                        "
                        @click="onClickOption(option)"
                        @favorite="onFavorite(option)"
                      ></option-item>
                    </template>

                    <template v-else>
                      <div class="empty-text">{{ $t('empty_text') }}</div>
                    </template>
                  </div>
                </z-scrollbar>
              </template>
            </z-chip-tabs>
          </div>
        </template>
      </z-card>
    </v-menu>
  </div>
</template>

<script>
import OptionItem from './OptionItem.vue'

export default {
  name: 'ZOptionsMenu',
  components: {OptionItem},
  props: {
    options: {
      type: Array,
      default: () => [],
    },

    title: {
      type: String,
      default: '',
    },

    hideFavorites: {
      type: Boolean,
      default: false,
    },
  },

  i18n: {
    messages: {
      en: {
        beginning: 'Beginning',
        all: 'All',
        favorites: 'Favorites',
        search_page: 'Search page',
        empty_text: 'No results found',
      },
      es: {
        beginning: 'Inicio',
        all: 'Todos',
        favorites: 'Favoritos',
        search_page: 'Página de búsqueda',
        empty_text: 'No se encontraron resultados',
      },
      pt: {
        beginning: 'Início',
        all: 'Todos',
        favorites: 'Favoritos',
        search_page: 'Buscar página',
        empty_text: 'Nenhum resultado encontrado',
      },
      fr: {
        beginning: 'Début',
        all: 'Tout',
        favorites: 'Favoris',
        search_page: 'Page de recherche',
        empty_text: 'Aucun résultat trouvé',
      },
      tr: {
        beginning: 'Başlangıç',
        all: 'Tümü',
        favorites: 'Favoriler',
        search_page: 'Arama sayfası',
        empty_text: 'Sonuç bulunamadı',
      },
    },
  },

  data() {
    return {
      tabValue: 'all',
      showMenu: false,
      showSearch: false,
      currentLevel: 1,
      breadcrumbsItems: [],
      offsetMenuX: 0,

      childrenL1: [],
      childrenL2: [],

      searchValue: '',
      searchOptions: [],
      favoritesSearchOptions: [],
    }
  },

  computed: {
    tabs() {
      const tabs = [
        {id: 'all', text: this.$t('all'), icon: '$all_inclusive_rounded'},
      ]

      if (!this.hideFavorites) {
        tabs.push({
          id: 'favorites',
          text: this.$t('favorites'),
          icon: '$star_rounded',
        })
      }

      return tabs
    },

    favorites() {
      return this.filterRecursive(this.options, {
        itemKey: 'favorite',
        term: true,
      }).filter((item) => !item.children?.length)
    },

    computedBreadcrumbs() {
      return [{title: this.$t('beginning')}, ...this.breadcrumbsItems]
    },

    firstLevelTransitions() {
      if (this.currentLevel === 1)
        return {
          foward: 'v-window-x-reverse-transition',
          back: 'v-window-x-transition',
        }

      return {
        foward: 'v-window-x-transition',
        back: 'v-window-x-reverse-transition',
      }
    },
  },

  watch: {
    showSearch() {
      this.searchValue = ''
    },
    currentLevel() {
      this.$nextTick(() => {
        this.$refs.scrollbar.$el.scrollTop = 0
      })
    },
    showMenu(val) {
      if (!val) {
        this.currentLevel = 1
        this.breadcrumbsItems = []
      }
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.offsetMenuX =
        this.$refs.activator.offsetParent.offsetWidth -
        this.$refs.activator.offsetLeft +
        30
    })
  },

  methods: {
    onClickLevel(option) {
      if (option.disabled) return

      if (option.children?.length) {
        this.childrenL1 = option.children
      } else {
        this.childrenL1 = []
        this.$emit('click:option', option)
        this.showMenu = false
        return
      }

      this.currentLevel++
      this.breadcrumbsItems.push({title: option.title})
    },

    onClickChildLevel(option) {
      if (option.disabled) return

      if (option.children?.length) {
        this.childrenL2 = option.children
      } else {
        this.childrenL2 = []
        this.$emit('click:option', option)
        this.showMenu = false
        return
      }

      this.currentLevel++
      this.breadcrumbsItems.push({title: option.title})
    },

    onClickOption(option) {
      if (option.disabled) return

      this.$emit('click:option', option)
      this.showMenu = false
    },

    onClickArrowBack() {
      this.currentLevel--
      this.breadcrumbsItems = this.breadcrumbsItems.slice(
        0,
        this.breadcrumbsItems.length - 1
      )
    },

    onClickBreadcrumb(item) {
      const index = this.computedBreadcrumbs.indexOf(item)

      this.breadcrumbsItems = this.computedBreadcrumbs.slice(1, index + 1)
      this.currentLevel = index + 1
    },

    onSearchPage(value) {
      /**
       * função recursiva para filtrar as paginas
       *
       * criamos dentro do método pois esse é o unico contexto onde ela será usada
       */
      function filterPages(arr, {itemKey, term}) {
        let matches = []

        if (!Array.isArray(arr)) {
          return matches
        }

        for (let i of arr) {
          if (
            i[itemKey]
              .split(' ')
              .join('')
              .toLowerCase()
              .includes(term.toLowerCase())
          ) {
            matches.push(i)
          }

          if (i.children?.length) {
            let childResults = filterPages(i.children, {itemKey, term})

            if (childResults.length) {
              matches.push(...childResults)
            }
          }
        }

        return matches
          .filter((item) => !item.children?.length)
          .sort((a, b) => {
            return a[itemKey].localeCompare(b[itemKey])
          })
      }

      this.searchValue = value

      if (value) {
        if (this.tabValue === 'all') {
          this.currentLevel = 1
          this.breadcrumbsItems = []

          this.searchOptions = filterPages(this.options, {
            itemKey: 'title',
            term: value,
          })
        } else {
          this.favoritesSearchOptions = filterPages(this.favorites, {
            itemKey: 'title',
            term: value,
          })
        }
      } else {
        this.searchOptions = []
        this.favoritesSearchOptions = []
      }
    },

    onFavorite(option) {
      if (option.disabled) return

      this.$emit('favorite:option', option)
    },

    isItemActive(item) {
      return item === this.breadcrumbsItems[this.breadcrumbsItems.length - 1]
    },

    filterRecursive(arr, {itemKey, term}) {
      let matches = []

      if (!Array.isArray(arr)) {
        return matches
      }

      for (let i of arr) {
        if (i[itemKey] === term) {
          matches.push(i)
        }

        if (i.children?.length) {
          let childResults = this.filterRecursive(i.children, {itemKey, term})
          if (childResults.length) {
            matches.push(...childResults)
          }
        }
      }

      return matches
    },

    setSearchVisibility(val) {
      this.showSearch = val

      if (val) {
        this.focusSearchBar()
      }
    },

    /**
     * @public
     *
     * @description Método para mostrar a barra de pesquisa programaticamente
     */
    focusSearchBar() {
      this.showMenu = true
      this.showSearch = true

      setTimeout(() => {
        this.$refs.searchInput.$el.querySelector('input').focus()
      }, 100)
    },
  },
}
</script>

<style scoped lang="scss">
.arrow-menu {
  transform: translateX(-50%);
  position: absolute;
  border-bottom: solid 8px var(--white) !important;
  border-right: solid 8px transparent;
  border-left: solid 8px transparent;
  top: 28px;
  left: 50%;
  height: 0px;
  width: 0px;
  z-index: 9;
}

:deep(.ps) {
  overflow: auto !important;
  scrollbar-gutter: stable !important;
  overscroll-behavior: contain !important;

  //hide browser scrollbar
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

::v-deep .v-breadcrumbs {
  flex-wrap: nowrap !important;
}

::v-deep .v-slide-group {
  z-index: 1;
  position: absolute;
  top: -35px;
  right: 20px;
  transition: none !important;
}

::v-deep .tab-container {
  padding: 5px 0px;
}

::v-deep .v-breadcrumbs__divider {
  padding: 0 5px !important;
}

.disabled-slider {
  ::v-deep .v-slide-group {
    opacity: 0;
    pointer-events: none !important;
  }
}

.options-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  //espaço para o scroll
  padding-right: 16px;

  &.empty {
    grid-template-columns: 1fr;
  }
}
.empty-text {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin: 20px 0px;
  font-size: 13px;
  font-weight: 400;
  line-height: 15px;
  color: var(--black-600);
}

::v-deep .breadcrumb-custom-item {
  font-size: 14px;
  line-height: 18px;
  font-weight: 400;
  color: var(--black);
  cursor: pointer;
  text-decoration: underline;
  max-width: 150px;

  &.active {
    cursor: default;
    margin-top: 1px;
    text-decoration: none;
  }
}

.fade-move,
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s ease-in-out;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fade-leave-active {
  position: absolute;
}
</style>
